// Minimal, dependency-free PDF text-layer reader for digitally generated documents (payslips,
// statements, schedules). It inflates FlateDecode content streams with the platform's
// DecompressionStream and reads the text operators (Tj, TJ, ', "). Scanned images have no text
// layer: the caller then offers manual entry in the review screen (or an OCR provider, if the owner
// configures one). Encrypted or malformed PDFs are reported, never guessed.

export interface PdfText { pages: string[]; encrypted: boolean; streams: number; decoded: number }

const latin1 = (b: Uint8Array) => { let s = ''; for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000)); return s; };

async function inflate(data: Uint8Array): Promise<Uint8Array | null> {
  for (const format of ['deflate', 'deflate-raw'] as const) {
    try {
      const ds = new DecompressionStream(format);
      const out = new Response(new Blob([data as BlobPart]).stream().pipeThrough(ds));
      return new Uint8Array(await out.arrayBuffer());
    } catch { /* try the next format */ }
  }
  return null;
}

/** decode a PDF literal string body (escapes, octal) */
function literal(s: string): string {
  return s.replace(/\\([nrtbf()\\]|[0-7]{1,3})/g, (_m, e: string) => {
    if (/^[0-7]+$/.test(e)) return String.fromCharCode(parseInt(e, 8));
    return ({ n: '\n', r: '\r', t: '\t', b: '\b', f: '\f', '(': '(', ')': ')', '\\': '\\' } as Record<string, string>)[e] ?? e;
  });
}
function hexString(h: string): string {
  const clean = h.replace(/\s+/g, '');
  const bytes: number[] = [];
  for (let i = 0; i < clean.length; i += 2) bytes.push(parseInt(clean.slice(i, i + 2).padEnd(2, '0'), 16));
  // UTF-16BE with BOM, or 2-byte codes → try UTF-16BE; otherwise Latin-1
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(new Uint8Array(bytes.slice(2)));
  return String.fromCharCode(...bytes.filter((b) => b >= 32 || b === 10));
}

/** Text shown by a content stream, one line per text line (Td, T-star and ET break lines). */
export function textFromContent(content: string): string {
  const out: string[] = [];
  let line = '';
  const re = /\((?:\\.|[^\\)])*\)|<[0-9A-Fa-f\s]*>|\[(?:\((?:\\.|[^\\)])*\)|<[0-9A-Fa-f\s]*>|[^\]])*\]|[A-Za-z'"*]+/g;
  let pending: string[] = [];
  for (const m of content.matchAll(re)) {
    const tok = m[0];
    if (tok.startsWith('(')) pending.push(literal(tok.slice(1, -1)));
    else if (tok.startsWith('<')) pending.push(hexString(tok.slice(1, -1)));
    else if (tok.startsWith('[')) {
      const parts: string[] = [];
      for (const x of tok.slice(1, -1).matchAll(/\((?:\\.|[^\\)])*\)|<[0-9A-Fa-f\s]*>|-?\d+(?:\.\d+)?/g)) {
        const t = x[0];
        if (t.startsWith('(')) parts.push(literal(t.slice(1, -1)));
        else if (t.startsWith('<')) parts.push(hexString(t.slice(1, -1)));
        else if (Number(t) < -200) parts.push(' '); // large negative kerning = word gap
      }
      pending.push(parts.join(''));
    } else if (tok === 'Tj' || tok === 'TJ') { line += pending.join(''); pending = []; }
    else if (tok === "'" || tok === '"') { out.push(line); line = pending.join(''); pending = []; }
    else if (tok === 'Td' || tok === 'TD' || tok === 'T*' || tok === 'ET' || tok === 'Tm') { if (line.trim()) out.push(line); line = ''; pending = []; }
    else pending = [];
  }
  if (line.trim()) out.push(line);
  return out.map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean).join('\n');
}

export async function pdfText(bytes: Uint8Array): Promise<PdfText> {
  const raw = latin1(bytes);
  if (!raw.startsWith('%PDF')) throw new Error('pdf.not_a_pdf');
  const encrypted = /\/Encrypt\s+\d+\s+\d+\s+R/.test(raw);
  const pages: string[] = [];
  let streams = 0, decoded = 0;
  const re = /<<((?:(?!>>)[\s\S])*?)>>\s*stream\r?\n/g;
  for (const m of raw.matchAll(re)) {
    const dict = m[1] ?? '';
    const start = (m.index ?? 0) + m[0].length;
    const lenMatch = /\/Length\s+(\d+)(?!\s+\d+\s+R)/.exec(dict);
    let end = lenMatch ? start + Number(lenMatch[1]) : raw.indexOf('endstream', start);
    if (end < start || end > raw.length) end = raw.indexOf('endstream', start);
    if (end < 0) continue;
    if (/\/Subtype\s*\/Image|\/XObject|\/FontFile|\/Type\s*\/(XRef|ObjStm|Metadata)/.test(dict)) continue;
    streams++;
    let data: Uint8Array | null = bytes.subarray(start, end);
    if (/\/FlateDecode/.test(dict)) data = await inflate(data);
    else if (/\/Filter/.test(dict)) continue; // other filters (DCT, LZW…) carry no readable text here
    if (!data) continue;
    const text = textFromContent(latin1(data));
    if (text) { decoded++; pages.push(text); }
  }
  return { pages, encrypted, streams, decoded };
}
